import React, { useState } from 'react';
import { Play, X, Star } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollReveal } from '../hooks/useAnimations';

const videoTestimonials = [
  {
    id: 1,
    name: "Priya Sharma",
    treatment: "Acne Treatment",
    rating: 5,
    thumbnail: null,
    initials: "PS",
    gradientFrom: "#D1A760",
    gradientTo: "#B89255",
    quote: "My skin has never looked better. Dr. Verma changed my life!",
  },
  {
    id: 2,
    name: "Rajesh Kumar",
    treatment: "Hair Transplant",
    rating: 5,
    thumbnail: null,
    initials: "RK",
    gradientFrom: "#0F172A",
    gradientTo: "#1E293B",
    quote: "Natural results that even my family couldn't tell apart.",
  },
  {
    id: 3,
    name: "Anita Verma",
    treatment: "Pigmentation",
    rating: 5,
    thumbnail: null,
    initials: "AV",
    gradientFrom: "#7C3AED",
    gradientTo: "#6D28D9",
    quote: "The pigmentation on my face is completely gone. Amazing!",
  },
  {
    id: 4,
    name: "Suresh Patel",
    treatment: "Laser Treatment",
    rating: 5,
    thumbnail: null,
    initials: "SP",
    gradientFrom: "#059669",
    gradientTo: "#047857",
    quote: "Painless procedure with incredible results. Highly recommend!",
  },
  {
    id: 5,
    name: "Meena Devi",
    treatment: "Chemical Peeling",
    rating: 5,
    thumbnail: null,
    initials: "MD",
    gradientFrom: "#DC2626",
    gradientTo: "#B91C1C",
    quote: "My skin glows now. Every session showed visible improvement.",
  },
  {
    id: 6,
    name: "Amit Gupta",
    treatment: "Scar Treatment",
    rating: 5,
    thumbnail: null,
    initials: "AG",
    gradientFrom: "#0284C7",
    gradientTo: "#0369A1",
    quote: "Years of acne scars faded in just a few sessions.",
  },
];

function VideoCard({ video, index, onPlay }) {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <div
      ref={ref}
      className={`group relative rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:shadow-[0_20px_60px_rgb(0,0,0,0.15)] hover:-translate-y-2 transition-all duration-500 cursor-pointer ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
      onClick={() => onPlay(video)}
      data-testid={`video-testimonial-card-${index}`}
    >
      {/* Thumbnail area */}
      <div
        className="relative aspect-[9/12] flex items-center justify-center"
        style={{ background: `linear-gradient(135deg, ${video.gradientFrom}, ${video.gradientTo})` }}
      >
        {/* Decorative circles */}
        <div className="absolute top-4 right-4 w-20 h-20 rounded-full border border-white/10" />
        <div className="absolute bottom-8 left-4 w-12 h-12 rounded-full border border-white/5" />

        {/* Initials avatar */}
        <div className="w-20 h-20 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20">
          <span className="text-2xl font-outfit font-semibold text-white">{video.initials}</span>
        </div>

        {/* Play button overlay */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="w-16 h-16 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform duration-300">
            <Play className="w-7 h-7 text-[#0F172A] ml-1" fill="#0F172A" />
          </div>
        </div>

        {/* Bottom gradient fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/70 to-transparent" />

        {/* Info overlay at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="flex gap-0.5 mb-1.5">
            {[...Array(video.rating)].map((_, i) => (
              <Star key={i} className="w-3 h-3 text-[#D1A760] fill-[#D1A760]" />
            ))}
          </div>
          <p className="text-white font-outfit font-medium text-sm">{video.name}</p>
          <p className="text-white/60 font-manrope text-xs">{video.treatment}</p>
        </div>
      </div>

      {/* Quote strip */}
      <div className="bg-white px-4 py-3">
        <p className="text-xs text-[#475569] font-manrope leading-relaxed line-clamp-2 italic">
          "{video.quote}"
        </p>
      </div>
    </div>
  );
}

function VideoModal({ video, onClose }) {
  if (!video) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
      data-testid="video-modal-overlay"
    >
      <div
        className="relative w-full max-w-2xl mx-4 bg-[#0F172A] rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="video-modal-content"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-white/20 transition-colors"
          data-testid="video-modal-close"
        >
          <X className="w-5 h-5 text-white" />
        </button>

        {/* Placeholder video area */}
        <div
          className="aspect-video flex flex-col items-center justify-center"
          style={{ background: `linear-gradient(135deg, ${video.gradientFrom}, ${video.gradientTo})` }}
        >
          <div className="w-24 h-24 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20 mb-4">
            <Play className="w-10 h-10 text-white ml-1" fill="white" />
          </div>
          <p className="text-white/60 font-manrope text-sm">Video testimonial coming soon</p>
        </div>

        {/* Patient info */}
        <div className="p-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-white font-outfit font-medium text-lg">{video.name}</h3>
              <p className="text-[#D1A760] font-manrope text-sm">{video.treatment}</p>
            </div>
            <div className="flex gap-0.5">
              {[...Array(video.rating)].map((_, i) => (
                <Star key={i} className="w-4 h-4 text-[#D1A760] fill-[#D1A760]" />
              ))}
            </div>
          </div>
          <p className="text-white/70 font-manrope text-sm italic">"{video.quote}"</p>
        </div>
      </div>
    </div>
  );
}

export default function TestimonialsSection() {
  const { t } = useLanguage();
  const [headerRef, headerVisible] = useScrollReveal(0.2);
  const [activeVideo, setActiveVideo] = useState(null);

  return (
    <>
      <section id="testimonials" className="py-20 md:py-32 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div
            ref={headerRef}
            className={`text-center mb-16 transition-all duration-700 ${
              headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <p className="text-sm font-manrope font-medium text-[#D1A760] tracking-wide uppercase mb-3">Patient Stories</p>
            <h2 className="font-outfit font-light text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] tracking-tight" data-testid="testimonials-title">
              {t('testimonials_title')}
            </h2>
            <p className="mt-4 text-base text-[#475569] font-manrope max-w-xl mx-auto">
              Real patients, real results. Watch their transformation journeys.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4" data-testid="video-testimonials-grid">
            {videoTestimonials.map((video) => (
              <VideoCard key={video.id} video={video} index={video.id - 1} onPlay={setActiveVideo} />
            ))}
          </div>
        </div>
      </section>

      <VideoModal video={activeVideo} onClose={() => setActiveVideo(null)} />
    </>
  );
}
